import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getAllComposers, getAllContentByOrgId, getAllSharedComposersWithoutQuery, getAllWithoutQueryComposers } from '../../api';
import { API_ROUTES } from '../../enums/API_ROUTES';
import { STATUS } from '../../enums/ENUMS';
import { VIDEO_TYPE, VIDEOFEED } from '../../enums/TYPES';
import { TComposerClip } from '../../components/Composer/ComposerRow';
import { getComposerDataByVideoFeedId } from '../../api/composersApi';
import { TClip } from '../../components/Timeline/TimelineClip';
import { SortOptions, SortType } from '../../enums/SORT';
import { getMyAmplifyComposersPageSortOptionAndType } from '../../helpers';

type TPaginatedResult = {
  documents: VIDEOFEED[];
  total: number;
};

type TQueryOptions = {
  page: number;
  limit: number;
  sort: SortType;
};

export type TSessionRows = {
  topics: {
    id: string;
    clips: {
      id: string;
    }[];
  }[];
  rows: TComposerClip[];
  topic?: TComposerClip;
  deletedRow?: boolean;
  deletedClips?: string[];
};

type TComposerControls = {
  playing: boolean;
  muted: boolean;
  volume: number;
  playbackRate: number;
  played: number;
  seeking: boolean;
  buffer: boolean;
  loadedSeconds: number;
  playedSeconds: number;
  loaded: number;
};

type TInitialState = {
  composers: VIDEOFEED[];
  composerRows: TComposerClip[];
  currentComposerVideos: VIDEO_TYPE[];
  currentComposerData: VIDEOFEED | null;
  total: number;
  page: number;
  limit: number;
  sort: SortType;
  sortOption: SortOptions;
  status: STATUS;
  showShareModalComposer: boolean;
  showShareWithMeModalComposer: boolean;
  shareComposer: VIDEOFEED | null;
  shareComposerForSharedWithMe: VIDEOFEED | null;
  composerPlayControl: {
    currentClipIndex: number;
    currentRowIndex: number;
    currentComposer: TComposerClip | VIDEOFEED | null;
    isDragging: boolean;
    composerVideoLength: number;
    composerControls: TComposerControls;
    currentMarkerPosition: number;
  };
  customTime: number;
  selectedMultipleClips: boolean;
  playingClip: TClip | null;
  currentSelectedComposerClips: TClip[]; // list of selected clips, including currently playing clip
  currentSelectedComposerRows: TComposerClip[]; // explicit row-level selection (matches Timeline semantics)
  lastSelectedComposerRowId: string;
  refreshTrigger: boolean;
  progressPosition: number;
  alreadyEnded: boolean;
  composerVideoClips: VIDEO_TYPE[] | null;
  anglesCount: number;
  commentFetchTrigger: number; // trigger for when we touch sessionRowsArray;
  videoLength: number;
  markMargin: number;
  rowWidth: number;
};

const initialState: TInitialState = {
  composers: [],
  composerRows: [],
  currentComposerVideos: [],
  currentComposerData: null,
  commentFetchTrigger: 0,
  total: 0,
  page: 0,
  limit: 10,
  sort: SortType.ASC,
  sortOption: SortOptions.UPDATED_AT,
  status: STATUS.IDLE,
  showShareModalComposer: false,
  showShareWithMeModalComposer: false,
  shareComposer: null,
  shareComposerForSharedWithMe: null,
  composerPlayControl: {
    currentClipIndex: 0,
    currentRowIndex: 0,
    currentComposer: null,
    isDragging: false,
    composerVideoLength: 0,
    currentMarkerPosition: 0,
    composerControls: {
      playing: false,
      muted: false,
      volume: 1,
      playbackRate: 1.0,
      played: 0,
      seeking: false,
      buffer: true,
      loadedSeconds: 0,
      playedSeconds: 0,
      loaded: 0,
    },
  },
  customTime: 0,
  selectedMultipleClips: false,
  playingClip: null,
  currentSelectedComposerClips: [],
  currentSelectedComposerRows: [],
  lastSelectedComposerRowId: '',
  refreshTrigger: false,
  progressPosition: 0,
  alreadyEnded: false,
  composerVideoClips: null,
  anglesCount: 0,
  videoLength: 0,
  markMargin: 12,
  rowWidth: 0,
};

export const getComposersThunk = createAsyncThunk(
  API_ROUTES.REFLEX_COMPOSER,
  async (data: { page: number; limit: number; search?: string; sort?: string; sortOption: SortOptions }) => {
    const response = await getAllComposers(data);
    return response.data;
  },
);

export const getAllComposersThunk = createAsyncThunk(`${API_ROUTES.REFLEX_COMPOSER}/all`, async () => {
  const res = getMyAmplifyComposersPageSortOptionAndType();
  if (res) {
    const sortAndOption = res.split('/');
    if (sortAndOption[0] && sortAndOption[1]) {
      const sortTypeQuery = sortAndOption[0] ? `?sort=${sortAndOption[0]}` : '';
      const sortFieldQuery = sortAndOption[1] ? `&sortField=${sortAndOption[1]}` : '';
      const response = await getAllWithoutQueryComposers(sortTypeQuery, sortFieldQuery);
      return response.data;
    }
  } else {
    const response = await getAllWithoutQueryComposers();
    return response.data;
  }
});

export const getAllSharedComposerThunk = createAsyncThunk(`${API_ROUTES.REFLEX_COMPOSER}/allSharedComposers`, async () => {
  const response = await getAllSharedComposersWithoutQuery();
  return response?.data;
});

export const getAllContentMyComposers = createAsyncThunk(`Get all content my composers`, async (orgId: number) => {
  const response = await getAllContentByOrgId(orgId, 0, 1000, SortType.DESC, SortOptions.NAME, '', null, '%22composer%22', null);
  return response?.data;
});

export const composerSlice = createSlice({
  name: 'composers',
  initialState,
  reducers: {
    setRowWidth: (state, action) => {
      state.rowWidth = action.payload;
    },
    updateMarkMargin: (state, action) => {
      state.markMargin = action.payload;
    },
    updateVideoLength: (state, action) => {
      state.videoLength = action.payload;
    },
    updateComposerVideoCLips: (state, action: PayloadAction<VIDEO_TYPE[] | null>) => {
      state.composerVideoClips = action.payload;
    },
    saveComposers: (state, action: PayloadAction<TPaginatedResult>) => {
      if (state.shareComposer) {
        state.shareComposer = null;
      }
      state.composers = action.payload.documents;
      state.total = action.payload.total;
    },
    saveQueryOptions: (state, action: PayloadAction<TQueryOptions>) => {
      if (state.shareComposer) {
        state.shareComposer = null;
      }
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.sort = action.payload.sort;
    },
    switchShareModalComposer: (state, action: PayloadAction<boolean>) => {
      state.showShareModalComposer = action.payload;
      state.showShareWithMeModalComposer = false;
    },
    switchSharedWithMeModalComposer: (state, action: PayloadAction<boolean>) => {
      state.showShareWithMeModalComposer = action.payload;
      state.showShareModalComposer = false;
    },
    selectShareComposer: (state, action: PayloadAction<VIDEOFEED>) => {
      state.shareComposer = action.payload;
    },
    selectedSharedWithMeComposer: (state, action: PayloadAction<VIDEOFEED>) => {
      state.shareComposerForSharedWithMe = action.payload;
    },
    removeSelectedComposer: (state) => {
      state.shareComposer = null;
      state.shareComposerForSharedWithMe = null;
    },
    // composer play control
    saveCurrentComposerVideos: (state, action: PayloadAction<VIDEO_TYPE[]>) => {
      state.currentComposerVideos = action.payload;
    },
    updateComposerRows: (state, action: PayloadAction<TComposerClip[]>) => {
      state.composerRows = action.payload;
    },
    updateComposerClipAngleLock: (state, { payload }: PayloadAction<{ clipId: string; locked_angle_id: number | null }>) => {
      const clips = [
        ...state.composerRows.flatMap((row) => row.clips),
        ...state.currentSelectedComposerClips,
        ...(state.currentComposerData?.topics.flatMap((row) => row.clips) || []),
      ];
      clips.forEach((clip) => {
        if (String(clip.id) === String(payload.clipId)) clip.locked_angle_id = payload.locked_angle_id;
      });
    },
    updateIsDragging: (state, action: PayloadAction<boolean>) => {
      state.composerPlayControl.isDragging = action.payload;
    },
    updateCurrentComposer: (state, action: PayloadAction<TComposerClip>) => {
      state.composerPlayControl.currentComposer = action.payload;
    },
    setComposerMarkerPosition: (state, action) => {
      state.composerPlayControl.currentRowIndex = action.payload.rowIndex;
      state.composerPlayControl.currentClipIndex = action.payload.clipIndex;
    },
    setFetchComments: (state) => {
      state.commentFetchTrigger = state.commentFetchTrigger + 1;
    },
    reorderComposerRows: (state, action) => {
      state.composerRows = action.payload;
    },
    clearComposerData: (state) => {
      state.composerRows = [];
      state.composerPlayControl.currentRowIndex = 0;
      state.composerPlayControl.currentClipIndex = 0;
      state.currentSelectedComposerClips = [];
      state.currentSelectedComposerRows = [];
      state.lastSelectedComposerRowId = '';
      state.selectedMultipleClips = false;
      state.composerPlayControl = {
        ...state.composerPlayControl,
        composerControls: {
          playing: false,
          muted: false,
          volume: 1,
          playbackRate: 1.0,
          played: 0,
          seeking: false,
          buffer: true,
          loadedSeconds: 0,
          playedSeconds: 0,
          loaded: 0,
        },
      };
    },
    updateComposerPlayControls: (state, action: PayloadAction<TComposerControls>) => {
      state.composerPlayControl.composerControls = action.payload;
    },
    updateCustomTime: (state, action: PayloadAction<number>) => {
      state.customTime = action.payload;
    },
    updateComposerVideoLength: (state, action: PayloadAction<number>) => {
      state.composerPlayControl.composerVideoLength = action.payload;
    },
    updateComposerPixelPosition: (state, action: PayloadAction<number>) => {
      state.composerPlayControl.currentMarkerPosition = action.payload;
    },
    updateSelectedMultipleClips: (state, action: PayloadAction<boolean>) => {
      state.selectedMultipleClips = action.payload;
    },
    updatePlayingClip: (state, action: PayloadAction<TClip | null>) => {
      state.playingClip = action.payload;
    },
    updateComposerRowSelection: (
      state,
      action: PayloadAction<{ selectedRows: TComposerClip[]; selectedClips: TClip[]; lastSelectedRowId: string }>,
    ) => {
      state.currentSelectedComposerRows = action.payload.selectedRows;
      state.currentSelectedComposerClips = action.payload.selectedClips;
      state.lastSelectedComposerRowId = action.payload.lastSelectedRowId;
      state.selectedMultipleClips = action.payload.selectedClips.length > 1;
    },
    updateCurrentSelectedComposerClips: (state, action: PayloadAction<TClip[]>) => {
      state.currentSelectedComposerClips = action.payload;
      state.currentSelectedComposerRows = [];
      state.lastSelectedComposerRowId = '';
    },
    updateCurrentSelectedComposerClipsKeepingRows: (state, action: PayloadAction<TClip[]>) => {
      state.currentSelectedComposerClips = action.payload;
      state.lastSelectedComposerRowId = '';
    },
    triggerRefresh: (state) => {
      state.refreshTrigger = !state.refreshTrigger;
    },
    updateProgressPosition: (state, action: PayloadAction<number>) => {
      state.progressPosition = action.payload;
    },
    updatePlayedSeconds: (state, action: PayloadAction<number>) => {
      state.composerPlayControl.composerControls.playedSeconds = action.payload;
    },
    updateAnglesCount: (state, action) => {
      state.anglesCount = action.payload;
    },
    updateSortType: (state, action) => {
      state.sort = action.payload;
    },
    updateSortOption: (state, action) => {
      state.sortOption = action.payload;
    },
    updateCurrentComposerData: (state, action: PayloadAction<VIDEOFEED>) => {
      state.currentComposerData = action.payload;
    },
    updateComposerReady: (state, action: PayloadAction<VIDEOFEED>) => {
      state.status = STATUS.IDLE;
      state.composerPlayControl.currentComposer = { ...action.payload };
      for (let i = 0; i < action.payload.topics.length; i++) {
        if (action.payload.topics[i]?.clips?.length > 0) {
          state.customTime = action.payload.topics[i].clips[0].time_start;
          state.composerPlayControl.currentRowIndex = i;
          state.composerPlayControl.currentClipIndex = 0;
          break;
        }
      }
      state.currentComposerVideos = action.payload.videos;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getComposersThunk.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getComposersThunk.fulfilled, (state, action: PayloadAction<TPaginatedResult>) => {
        state.status = STATUS.IDLE;
        state.composers = action.payload.documents;
        state.total = action.payload.total;
      })
      .addCase(getComposersThunk.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
    builder
      .addCase(getAllComposersThunk.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getAllComposersThunk.fulfilled, (state, action: PayloadAction<TPaginatedResult>) => {
        state.status = STATUS.IDLE;
        state.composers = action.payload.documents;
        state.total = action.payload.total;
      })
      .addCase(getAllComposersThunk.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
    builder
      .addCase(getAllSharedComposerThunk.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getAllSharedComposerThunk.fulfilled, (state, action: PayloadAction<TPaginatedResult>) => {
        state.status = STATUS.IDLE;
        state.composers = action.payload.documents;
        state.total = action.payload.total;
      })
      .addCase(getAllSharedComposerThunk.rejected, (state) => {
        state.status = STATUS.FAILED;
      })
      .addCase(getAllContentMyComposers.pending, (state) => {
        state.status = STATUS.LOADING;
      })
      .addCase(getAllContentMyComposers.fulfilled, (state, action: PayloadAction<TPaginatedResult>) => {
        state.status = STATUS.IDLE;
        state.composers = action.payload.documents;
        state.total = action.payload.total;
      })
      .addCase(getAllContentMyComposers.rejected, (state) => {
        state.status = STATUS.FAILED;
      });
  },
});

export const {
  saveComposers,
  saveQueryOptions,
  switchShareModalComposer,
  switchSharedWithMeModalComposer,
  selectShareComposer,
  removeSelectedComposer,
  selectedSharedWithMeComposer,
  // playNextComposerClip,
  // playPreviousComposerClip,
  setComposerMarkerPosition,
  clearComposerData,
  updateIsDragging,
  updateCurrentComposer,
  reorderComposerRows,
  updateComposerRows,
  updateComposerClipAngleLock,
  updateComposerPlayControls,
  updateCustomTime,
  updateComposerVideoLength,
  updateComposerPixelPosition,
  updateSelectedMultipleClips,
  updatePlayingClip,
  updateComposerRowSelection,
  updateCurrentSelectedComposerClips,
  updateCurrentSelectedComposerClipsKeepingRows,
  saveCurrentComposerVideos,
  triggerRefresh,
  updateProgressPosition,
  updatePlayedSeconds,
  updateComposerVideoCLips,
  updateAnglesCount,
  updateSortType,
  updateSortOption,
  updateVideoLength,
  updateCurrentComposerData,
  setFetchComments,
  updateMarkMargin,
  setRowWidth,
  updateComposerReady,
} = composerSlice.actions;

export default composerSlice.reducer;
